import AppDispatcher from '../dispatcher'
import type { Document } from '../entities/Document'
import type { Draft, Post } from '../entities/Draft/Draft'
import type {
  FacebookChannelData,
  GoogleBusinessChannelData,
  InstagramChannelData,
  LinkedinChannelData,
  StartPageChannelData,
  ThreadsChannelData,
  TiktokChannelData,
  TwitterChannelData,
  YoutubeChannelData,
} from '../entities/Draft/types'
import type { ComposerImageWorkspaceChange } from '../entities/ComposerImageWorkspaceChange'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'
import type { Gif, Image, Video } from '../entities/factories'
import { ActionTypes } from '../state/ActionTypes'
import ComposerStore from '../stores/ComposerStore'
import type { AttachmentTypes } from '../AppConstants'
import Scraper from '../utils/Scraper'
import { logError } from '~publish/legacy/utils/logError'

const ComposerActionCreators = {
  enable: (id: string, markAppAsLoadedWhenDone = false): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_ENABLE,
      id,
      markAppAsLoadedWhenDone,
    })
  },

  disable: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_DISABLE,
      id,
    })
  },

  expand: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_EXPAND,
      id,
    })
  },

  collapse: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_COLLAPSE,
      id,
    })
  },

  updateDraftHasSavingError: (id: string, hasSavingError: boolean): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.UPDATE_DRAFT_HAS_SAVING_ERROR,
      id,
      hasSavingError,
    })
  },

  updateDraftIsSaved: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.UPDATE_DRAFT_IS_SAVED,
      id,
    })
  },

  setIsTwitterPremium: (id: string, isTwitterPremium: boolean): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_SET_IS_TWITTER_PREMIUM,
      id,
      isTwitterPremium,
    })
  },

  setMastodonMaxCharacters: (id: string, maxCharacters?: number): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_SET_MASTODON_MAX_CHARACTERS,
      id,
      maxCharacters,
    })
  },

  /**
   * Note: we're using a synchronous "dispatch" here, despite it not being
   * idiomatic, to prevent race conditions when updating the editor state:
   * if the state is updated in the editor component and takes some time to
   * reach the store because of an async dispatch, then any change made to
   * the editor state within the store during that time period will be lost.
   * This is the reason why this should be done synchronously.
   */
  updateDraftEditorState: (id: string, editorState: BufferEditor): void => {
    ComposerStore.syncDispatch({
      action: {
        actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_EDITOR_STATE,
        id,
        editorState,
      },
    })
  },

  updateDraftSourceLink: (id: string, url: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_SOURCE_LINK,
      id,
      url,
    })
  },

  updateDraftSourceLinkData: (() => {
    let currentCanonicalUrl: string

    return (id: string, url: string): void => {
      const canonicalUrl = ComposerStore.getCanonicalUrl(url)

      currentCanonicalUrl = canonicalUrl

      void Scraper.scrape(canonicalUrl)
        .then((scrapedData) => {
          const doesScrapedDataStillMatchSourceUrl =
            currentCanonicalUrl === canonicalUrl

          if (!doesScrapedDataStillMatchSourceUrl) {
            return undefined
          }

          const linkData = { ...scrapedData, url }

          AppDispatcher.handleViewAction({
            actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_SOURCE_LINK_DATA,
            id,
            linkData,
          })
          return undefined
        })
        .catch(logError)
    }
  })(),

  draftUpdated: (): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_DRAFT_UPDATED,
    })
  },

  updateDraftCommentCharacterCount: (
    id: string,
    { didEditorStateChange = true } = {},
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_COMMENT_CHARACTER_COUNT,
      id,
      didEditorStateChange,
    })
  },

  updateDraftTitleCharacterCount: (
    id: string,
    { didEditorStateChange = true } = {},
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_TITLE_CHARACTER_COUNT,
      id,
      didEditorStateChange,
    })
  },

  updateDraftUpdateType: (id: string, updateType: string | null): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_UPDATE_TYPE,
      id,
      updateType,
    })
  },

  updateDraftAddPostToThread: (id: string, offset?: number): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_ADD_POST_TO_THREAD,
      id,
      offset,
    })
  },

  updatePostText: (id: string, text: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_POST_TEXT,
      id,
      text,
    })
  },

  setActiveThreadId: (threadId: number): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_SET_ACTIVE_THREAD_ID,
      threadId,
    })
  },

  switchActiveThreadEditor: (id: string, threadId: number): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_SWITCH_ACTIVE_THREAD_EDITOR,
      id,
      threadId,
    })
  },

  deleteThreadedDraft: (
    id: string,
    threadId?: number,
    mergeThreads?: boolean,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_DELETE_THREADED_DRAFT,
      id,
      threadId,
      mergeThreads,
    })
  },

  updateThreadOrder: (id: string, newThreadOrder: Post[]): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_THREAD_ORDER,
      id,
      newThreadOrder,
    })
  },

  updateDraftMediaOrder: (
    id: string,
    dragIndex: number,
    hoverIndex: number,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_MEDIA_ORDER,
      id,
      dragIndex,
      hoverIndex,
    })
  },

  updateDraftGoogleBusinessData: (
    googleBusinessData: GoogleBusinessChannelData,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_GOOGLE_BUSINESS_DATA,
      googleBusinessData,
    })
  },

  updateDraftMastodonData: (mastodonData: { spoilerText?: string }): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_MASTODON_DATA,
      mastodonData,
    })
  },

  updateDraftStartPageData: (startPageData: StartPageChannelData): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_START_PAGE_DATA,
      startPageData,
    })
  },

  updateDraftInstagramData: (instagramData: InstagramChannelData): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_INSTAGRAM_DATA,
      instagramData,
    })
  },

  updateDraftLinkedinData: (linkedinData: LinkedinChannelData): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_LINKEDIN_DATA,
      linkedinData,
    })
  },

  updateDraftFacebookData: (facebookData: FacebookChannelData): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_FACEBOOK_DATA,
      facebookData,
    })
  },

  updateDraftsIsReminder: (isReminder: boolean): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFTS_IS_REMINDER,
      isReminder,
    })
  },

  updateDraftIsReminder: (draftId: string, isReminder: boolean): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_IS_REMINDER,
      draftId,
      isReminder,
    })
  },

  updateDraftSelectedStickers: (
    id: string,
    selectedStickers: string[],
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_SELECTED_STICKERS,
      id,
      selectedStickers,
    })
  },

  updateDraftInitialStickers: (): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFTS_INITIAL_STICKERS,
    })
  },

  updateDraftYoutubeData: (youtubeData: Partial<YoutubeChannelData>): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_YOUTUBE_DATA,
      youtubeData,
    })
  },

  updateDraftTiktokData: (tiktokData: TiktokChannelData): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_TIKTOK_DATA,
      tiktokData,
    })
  },

  updateDraftTwitterData: (twitterData: TwitterChannelData): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_TWITTER_DATA,
      twitterData,
    })
  },

  updateDraftThreadsData: (threadsData: ThreadsChannelData): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_THREADS_DATA,
      threadsData,
    })
  },

  updateSidebarOnTop: (
    id: string | null,
    sidebarOnTop: string | null,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_SIDEPANEL_ONTOP,
      id,
      sidebarOnTop,
    })
  },

  /**
   * Updates the should shorten links flag for drafts
   * If id is defined, the matching draft will be updated
   * Otherwise, all drafts will be updated
   */
  updateShouldShortenLinks: (
    id: string | undefined,
    shouldShortenLinks: boolean,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_SHOULD_SHORTEN_LINKS,
      id,
      shouldShortenLinks,
    })
  },

  /**
   * Apply link shortening to any links that have not been shortened yet,
   * and use cached values for the rest of the links that were previously shortened.
   */
  updateRegenerateShortenedLinks: (
    id: string,
    shouldShortenLinks: boolean,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_REGENERATE_SHORTENED_LINKS,
      id,
      shouldShortenLinks,
    })
  },

  parseDraftTextLinks: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_PARSE_DRAFT_TEXT_LINKS,
      id,
    })
  },

  parseMastodonText: (): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_PARSE_MASTODON_TEXT,
    })
  },

  updateDraftLink: (id: string, url: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_LINK_DATA,
      id,
      linkData: { url },
      meta: {
        isNewLinkAttachment: true,
        comesFromDirectUserAction: false,
      },
    })
  },

  updateDraftLinkTitle: (id: string, title: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_LINK_DATA,
      id,
      linkData: {
        title,
        // Remove scraping error if attachment is edited by the user
        scrapingError: undefined,
      },
      meta: {
        isNewLinkAttachment: false,
        comesFromDirectUserAction: true,
      },
    })
  },

  updateDraftLinkDescription: (id: string, description: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_LINK_DATA,
      id,
      linkData: {
        description,
        // Remove scraping error if attachment is edited by the user
        scrapingError: undefined,
      },
      meta: {
        isNewLinkAttachment: false,
        comesFromDirectUserAction: true,
      },
    })
  },

  scrapeDraftLinkData: (
    id: string,
    url: string,
    originalThumbnailUrl?: string,
  ): void => {
    const canonicalUrl = ComposerStore.getCanonicalUrl(url)

    void Scraper.scrape(canonicalUrl)
      .then((scrapedData) => ({
        url,
        title: scrapedData.title || '',
        description: scrapedData.description || '',
        availableThumbnails: scrapedData.images || [],
        originalThumbnailUrl,
        scrapingError:
          scrapedData.success === false
            ? scrapedData.message
              ? scrapedData.message
              : 'Failed to scrape link'
            : undefined,
      }))
      .then((linkData) => {
        AppDispatcher.handleViewAction({
          actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_LINK_DATA,
          id,
          linkData,
          meta: {
            isNewLinkAttachment: false,
            comesFromDirectUserAction: false,
          },
        })
        return undefined
      })
      .catch(logError)
  },

  selectNextLinkThumbnail: (draftId: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_NEXT_LINK_THUMBNAIL,
      draftId,
    })
  },

  selectPreviousLinkThumbnail: (draftId: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_PREVIOUS_LINK_THUMBNAIL,
      draftId,
    })
  },

  draftTextLinkUnshortened: (id: string, unshortenedLink: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_DRAFT_LINK_UNSHORTENED,
      id,
      unshortenedLink,
    })
  },

  draftTextLinkShortened: (
    id: string,
    unshortenedLink: string,
    shortLink: string,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_DRAFT_LINK_SHORTENED,
      id,
      link: unshortenedLink,
      shortLink,
    })
  },

  draftTextLinkReshortened: (
    id: string,
    unshortenedLink: string,
    shortLink: string,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_DRAFT_LINK_RESHORTENED,
      id,
      shortLink,
      link: unshortenedLink,
    })
  },

  updateDraftLinkAvailableImages: (id: string, url: string): void => {
    const canonicalUrl = ComposerStore.getCanonicalUrl(url)

    void Scraper.scrape(canonicalUrl)
      .then(({ images = [] }) => {
        if (images.length === 0) {
          return undefined
        }

        AppDispatcher.handleViewAction({
          actionType: ActionTypes.COMPOSER_ADD_DRAFT_AVAILABLE_IMAGES,
          id,
          images,
          sourceLink: url,
        })
        return undefined
      })
      .catch(logError)
  },

  removeDraftLinkAvailableImages: (id: string, sourceLink: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_REMOVE_DRAFT_AVAILABLE_IMAGES,
      id,
      sourceLink,
    })
  },

  toggleAttachment: (
    id: string,
    attachmentType: keyof typeof AttachmentTypes,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_TOGGLE_DRAFT_ATTACHMENT,
      id,
      attachmentType,
    })
  },

  disableAttachment: (
    id: string,
    attachmentType: keyof typeof AttachmentTypes,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_DISABLE_DRAFT_ATTACHMENT,
      id,
      attachmentType,
      didEditorStateChange: false,
    })
  },

  attachmentToggled: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_DRAFT_ATTACHMENT_TOGGLED,
      id,
    })
  },

  addDraftImage: (id: string, image: Image): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_ADD_DRAFT_IMAGE,
      id,
      image,
    })
  },

  draftImageAdded: (id: string, url: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_DRAFT_IMAGE_ADDED,
      id,
      url,
    })
  },

  addDraftVideo: (id: string, video: Video): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_ADD_DRAFT_VIDEO,
      id,
      video,
    })
  },

  addDraftGif: (id: string, gif: Gif): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_ADD_DRAFT_GIF,
      id,
      gif,
    })
  },

  replaceDraftGif: ({
    draftId,
    expectedGif,
    replacement,
  }: {
    draftId: string
    expectedGif: Gif
    replacement: Gif
  }): boolean => {
    if (ComposerStore.getDraft(draftId)?.gif !== expectedGif) return false

    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_REPLACE_DRAFT_GIF,
      draftId,
      expectedGif,
      replacement,
    })
    return true
  },

  updateDraftImageUserTags: (
    id: string,
    userTags: Image['userTags'],
    mediaUrl?: string,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_IMAGE_USER_TAGS,
      id,
      userTags,
      mediaUrl,
    })
  },

  updateDraftTitle: (id: string, title: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_TITLE,
      id,
      title,
    })
  },

  updateDraftLinkThumbnail: (id: string, thumbnail: Image): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_LINK_THUMBNAIL,
      id,
      thumbnail,
    })
  },

  updateDraftVideoThumbnail: (id: string, thumbnail: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_VIDEO_THUMBNAIL,
      id,
      thumbnail,
    })
  },

  updateDraftVideoTitle: (
    id: string,
    title: string,
    video?: Draft['video'],
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_VIDEO_TITLE,
      id,
      title,
      video,
    })
  },

  draftVideoAdded: (id: string, video: Draft['video']): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_DRAFT_VIDEO_ADDED,
      id,
      video,
    })
  },

  draftGifAdded: (id: string, url: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_DRAFT_GIF_ADDED,
      id,
      url,
    })
  },

  removeDraftImage: (id: string, image: Image): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_REMOVE_DRAFT_IMAGE,
      id,
      image,
    })
  },

  setLinkedInCarouselTitle: (title = ''): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_SET_LINKEDIN_CAROUSEL_TITLE,
      title,
    })
  },

  addDraftDocument: (document: Document, draftId?: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_ADD_DRAFT_UPLOADED_DOCUMENT,
      draftId: draftId ?? null,
      document,
    })
  },

  removeDraftDocument: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_REMOVE_DRAFT_DOCUMENT,
      id,
    })
  },

  // `video` selects one entry out of a multi-video draft; omitted, this
  // clears every video, matching every single-video caller's expectation.
  removeDraftVideo: (id: string, video?: Video): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_REMOVE_DRAFT_VIDEO,
      id,
      video,
    })
  },

  removeDraftGif: (id: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_REMOVE_DRAFT_GIF,
      id,
    })
  },

  updateImageAltText: (image: Image, altText: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_IMAGE_ALT_TEXT,
      image,
      altText,
    })
  },

  applyImageWorkspaceChanges: ({
    draftId,
    changes,
  }: {
    draftId: string
    changes: readonly ComposerImageWorkspaceChange[]
  }): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_APPLY_IMAGE_WORKSPACE_CHANGES,
      draftId,
      changes,
    })
  },

  replaceDraftImages: ({
    draftId,
    oldUrls,
    images,
  }: {
    draftId: string
    oldUrls: readonly string[]
    images: Image[]
  }): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_REPLACE_DRAFT_IMAGES,
      draftId,
      oldUrls,
      images,
    })
  },

  setImageBeingReplaced: ({
    imageUrl,
    isBeingReplaced,
    serviceId,
  }: {
    imageUrl: string
    isBeingReplaced: boolean
    serviceId: string
  }): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_SET_IMAGE_BEING_REPLACED,
      imageUrl,
      isBeingReplaced,
      serviceId,
    })
  },

  applyOmniUpdate: (): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_APPLY_OMNI_UPDATE,
    })
  },

  // Flush the omni draft's content onto the per-channel drafts without leaving
  // omni mode, so the save path reads the text the user typed in the omni editor.
  applyOmniContentToDrafts: (): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_APPLY_OMNI_CONTENT,
    })
  },

  hideSuggestedMedia: (): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_HIDE_SUGGESTED_MEDIA,
    })
  },

  updateDraftsScheduledAt: (
    scheduledAt: number | null,
    isPinnedToSlot = false,
  ): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFTS_SCHEDULED_AT,
      scheduledAt,
      isPinnedToSlot,
    })
  },

  updateDraftListPlaces: (id: string, places: Draft['places']): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_LIST_PLACES,
      id,
      places,
    })
  },

  insertHashtagInDraftCaption: (text: string): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_INSERT_HASHTAG_TO_CAPTION,
      text,
    })
  },

  updateAiAssist: (): void => {
    AppDispatcher.handleViewAction({
      actionType: ActionTypes.COMPOSER_UPDATE_AI_ASSISTED,
    })
  },
}

export default ComposerActionCreators
